import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { RemoveButton } from 'Shared/editor/list_row';
import { AuthorPicker } from 'Shared/editor/author_picker';
import { FIELD_HINT_CLASS, SECTION_HEADING_CLASS } from 'Shared/editor/field';
import { LanguageSection } from 'Shared/editor/language_section';
import { EditPermissionsSection, TagsSection } from 'Shared/editor/metadata_sections';
import type { GuideEditFields } from './guide_patch';
import { searchTeams, searchUsers } from 'Shared/editor/suggest_api';
import { SuggestInput } from 'Shared/editor/SuggestInput';
import type { GuideEditMetadataProps } from './types';
import {
   type GuideTeam,
   type GuideUser,
   useEditGuideTeam,
   useEditGuideUser,
   useGuideTeams,
   useGuideUsers,
} from './use_guide_edit_api';

/**
 * The Metadata tab: edit permissions and tags (applied by the page Save
 * button), plus the private-guide team/user allowlists (applied immediately
 * through their individual API endpoints). Admin + moderator only; regular
 * editors set flags from the Details tab instead.
 *
 * The permissions and tags sections are the wiki editor's — same styling,
 * same AjaxIO contracts, registered on this page too.
 */
export function MetadataTab({
   guideid,
   metadata,
   initialAuthor,
   fields,
   onChange,
   onBeforeLeave,
}: {
   guideid: number;
   metadata: GuideEditMetadataProps;
   /** The author as loaded, so the picker can offer a revert. */
   initialAuthor: GuideEditFields['author'];
   fields: GuideEditFields;
   onChange: (fields: GuideEditFields) => void;
   /** Runs before a language switch navigates away, to drop the page guard. */
   onBeforeLeave: () => void;
}) {
   return (
      <div className="flex max-w-3xl flex-col gap-8">
         {metadata.patrol && (
            <EditPermissionsSection
               maxAllowed={metadata.patrol.maxAllowed}
               maxDisplayed={metadata.patrol.maxDisplayed}
               levels={metadata.patrol.levels}
               helpUrl={metadata.patrol.helpUrl}
               value={fields.patrolThreshold}
               onChange={patrolThreshold => onChange({ ...fields, patrolThreshold })}
            />
         )}

         {metadata.canChangeAuthor && fields.author && (
            <AuthorSection
               initial={initialAuthor}
               author={fields.author}
               onChange={author => onChange({ ...fields, author })}
            />
         )}

         {metadata.tags && (
            <TagsSection
               maxLength={metadata.tags.maxLength}
               value={fields.tags}
               onChange={tags => onChange({ ...fields, tags })}
            />
         )}

         {metadata.canChangePrivacy && (
            <>
               <TeamsSection guideid={guideid} />
               <UsersSection guideid={guideid} />
            </>
         )}

         {metadata.language && (
            <LanguageSection doctype="guide" {...metadata.language} onBeforeLeave={onBeforeLeave} />
         )}
      </div>
   );
}

/**
 * Change the guide's author. Admin-only (the PATCH enforces it with
 * canIEditGuideAuthor); staged like every other field, so picking a user
 * updates the row immediately and the page Save applies it. The row shows
 * whatever avatar the pick carried — a user without one keeps the icon
 * placeholder until a save reseeds the field with the server's
 * default-avatar rendition.
 */
function AuthorSection({
   initial,
   author,
   onChange,
}: {
   initial: GuideEditFields['author'];
   author: NonNullable<GuideEditFields['author']>;
   onChange: (author: GuideEditFields['author']) => void;
}) {
   return (
      <div>
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{_js('Author')}</h2>
         <p className={cn('mb-2', FIELD_HINT_CLASS)}>
            {_js('The member credited as this guide’s author.')}
         </p>
         <AuthorPicker
            queryKey="guide-author-suggest"
            initial={initial}
            value={author}
            onChange={onChange}
         />
      </div>
   );
}

function TeamsSection({ guideid }: { guideid: number }) {
   const [query, setQuery] = useState('');
   const teams = useGuideTeams(guideid, true);
   const editTeam = useEditGuideTeam(guideid);
   const list = teams.data ?? [];
   // A pending add has no existing row to point at; only a pending remove
   // identifies one, via the mutation's own variables.
   const pendingVariables = editTeam.variables;
   const pendingTeamid =
      editTeam.isPending && pendingVariables?.action === 'remove' ? pendingVariables.teamid : null;

   return (
      <AllowListSection
         title={_js('Teams')}
         description={_js(
            'Members of these teams can view this guide while private and make unpatrolled edits.'
         )}
         rows={list.map((team: GuideTeam) => ({
            key: team.teamid,
            label: team.name,
            imageUrl: team.image?.mini ?? null,
         }))}
         icon="users"
         isPending={editTeam.isPending}
         pendingKey={pendingTeamid}
         error={editTeam.isError ? _js('The team change did not apply. Try again.') : null}
         onRemove={teamid => editTeam.mutate({ teamid: Number(teamid), action: 'remove' })}
         search={
            <SuggestInput
               queryKey="guide-team-suggest"
               className="mt-2"
               value={query}
               onValueChange={setQuery}
               placeholder={_js('Search for a team...')}
               ariaLabel={_js('Search for a team')}
               fetchSuggestions={searchTeams}
               imageShape="avatar"
               excludeKeys={list.map(team => team.teamid)}
               minQueryLength={3}
               onSelect={suggestion => {
                  editTeam.mutate({ teamid: Number(suggestion.key), action: 'add' });
                  setQuery('');
               }}
            />
         }
      />
   );
}

function UsersSection({ guideid }: { guideid: number }) {
   const [query, setQuery] = useState('');
   const users = useGuideUsers(guideid, true);
   const editUser = useEditGuideUser(guideid);
   const list = users.data ?? [];
   // A pending add has no existing row to point at; only a pending remove
   // identifies one, via the mutation's own variables.
   const pendingVariables = editUser.variables;
   const pendingUserid =
      editUser.isPending && pendingVariables?.action === 'remove' ? pendingVariables.userid : null;

   return (
      <AllowListSection
         title={_js('Collaborators')}
         description={_js(
            'These collaborators can view this guide while private and make unpatrolled edits.'
         )}
         rows={list.map((user: GuideUser) => ({
            key: user.userid,
            label: user.unique_username
               ? `${user.username} (@${user.unique_username})`
               : user.username,
            imageUrl: user.image?.mini ?? null,
         }))}
         icon="user"
         isPending={editUser.isPending}
         pendingKey={pendingUserid}
         error={editUser.isError ? _js('The collaborator change did not apply. Try again.') : null}
         onRemove={userid => editUser.mutate({ userid: Number(userid), action: 'remove' })}
         search={
            <SuggestInput
               queryKey="guide-user-suggest"
               className="mt-2"
               value={query}
               onValueChange={setQuery}
               placeholder={_js('Search for a collaborator...')}
               ariaLabel={_js('Search for a collaborator')}
               fetchSuggestions={searchUsers}
               imageShape="avatar"
               excludeKeys={list.map(user => user.userid)}
               onSelect={suggestion => {
                  editUser.mutate({ userid: Number(suggestion.key), action: 'add' });
                  setQuery('');
               }}
            />
         }
      />
   );
}

function AllowListSection({
   title,
   description,
   rows,
   icon,
   search,
   isPending,
   pendingKey,
   error,
   onRemove,
}: {
   title: string;
   description: string;
   rows: { key: number; label: string; imageUrl: string | null }[];
   /** Placeholder shown for a row without its own avatar. */
   icon: 'user' | 'users';
   search: ReactNode;
   isPending: boolean;
   /** The row a pending remove targets, from the mutation's own variables. */
   pendingKey: number | null;
   error: string | null;
   onRemove: (key: number) => void;
}) {
   return (
      <div>
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{title}</h2>
         <p className={cn('mb-2', FIELD_HINT_CLASS)}>{description}</p>
         <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {rows.map(row => {
               const isRowPending = row.key === pendingKey;
               return (
                  <li
                     key={row.key}
                     className={cn(
                        'flex items-center gap-2 rounded-lg border border-gray-300 p-1.5 text-sm',
                        isRowPending && 'opacity-60'
                     )}
                  >
                     {row.imageUrl ? (
                        <img
                           src={row.imageUrl}
                           alt=""
                           className="size-6 shrink-0 rounded-full object-cover"
                        />
                     ) : (
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                           <FaIcon name={icon} className="size-3.5" aria-hidden />
                        </span>
                     )}
                     <span className="min-w-0 flex-1 truncate">{row.label}</span>
                     {isRowPending ? (
                        <span className="flex size-7 shrink-0 items-center justify-center">
                           <FaIcon
                              name="spinner"
                              className="size-3.5 animate-spin text-gray-400 motion-reduce:animate-none"
                              aria-hidden
                           />
                           <span className="sr-only">{_js('Removing %1…', row.label)}</span>
                        </span>
                     ) : (
                        <RemoveButton
                           label={_js('Remove %1', row.label)}
                           disabled={isPending}
                           onClick={() => onRemove(row.key)}
                        />
                     )}
                  </li>
               );
            })}
         </ul>
         {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
         {search}
      </div>
   );
}
